import type { BrandId } from 'utils/override';
import type { CmsTiProviderHeroComponent } from '$/(cms)/_modules/cmsService/cmsServiceGql/mapper/components/TiProviderHero';
import type { CmsBaseComponent } from '$/(cms)/_modules/cmsService/types';
import type { CmsPlanTierSelectorComponent } from '$/(cms)/_modules/cmsService/types/components';

export type ResolvedTiProviderHero = {
  /** Provider owned by M3 (PlanTierSelector) — the single source of truth. */
  provider: CmsPlanTierSelectorComponent['provider'] | undefined;
  /** Hero entries carrying a non-empty title, in document order. */
  heroes: CmsTiProviderHeroComponent[];
  /** First hero entry to render (the document outline allows a single `<h1>`). */
  hero: CmsTiProviderHeroComponent | undefined;
  /** True when the hero should render: TI brand, a hero entry, a provider image. */
  hasVisibleTiProviderHero: boolean;
};

/**
 * Resolves the TI Provider Hero for a page or article. The provider is owned by
 * M3 (PlanTierSelector) — the single source of truth per CMS-1724 (Decision A)
 * — and is read regardless of M3's own `visible` flag, so hiding the selector
 * block never drops the provider the hero displays. Shared by PageContent and
 * ArticleContent so both containers source and gate the hero identically.
 */
export const resolveTiProviderHero = (
  components: readonly CmsBaseComponent[] | undefined,
  brand: BrandId,
): ResolvedTiProviderHero => {
  const list = components ?? [];
  const isTiProviderHeroBrand = brand === 'singsaver' || brand === 'moneyhero';

  const planTierSelector = list.find(
    (component): component is CmsPlanTierSelectorComponent =>
      component?.__typename ===
      'ComponentPagesComponentPlanTierSelectorComponent',
  );
  const provider = planTierSelector?.provider;

  const heroes = list.filter(
    (component): component is CmsTiProviderHeroComponent =>
      component?.__typename ===
        'ComponentPagesComponentTiProviderHeroComponent' &&
      !!component.title?.trim(),
  );

  // Hidden when there is no M3 provider image (nothing to show) or no hero
  // entry with a title.
  const hasVisibleTiProviderHero =
    isTiProviderHeroBrand &&
    heroes.length > 0 &&
    !!provider?.providerImage?.trim();

  return {
    provider,
    heroes,
    hero: heroes[0],
    hasVisibleTiProviderHero,
  };
};
